<template>
  <onboarding-form-layout
    :title="$t('incremental_onboarding.registration.title')"
    :loading="submitting"
    :show-previous="!isUpdate"
    @proceed="proceed"
    @previous="$emit('previous')"
  >
    <ErrorCodeNotification v-if="errorCodes && errorCodes.length > 0" :error-codes="errorCodes" />
    <div class="s-flex s-flex-col s-gap-4">
      <InputField
        v-for="field in getApplicableCountrySpecificApiFields"
        :label="$t(getCountrySpecificApiFields[field].label[getCountry] || getCountrySpecificApiFields[field].label.default)"
        :key="field"
        :value="getCountrySpecificApiFields[field].value"
        :set-value="(event) => setCountrySpecificField({field, event})"
        :error="getErrors[field]"
        :id="field"
        :show-mandatory="true"
        :data-testid="field"
        :hint="$t(getCountrySpecificApiFields[field].instruction[getCountry] || getCountrySpecificApiFields[field].instruction.default)"
        class="fs-mask"
      />
    </div>

    <DateInput
      :label="$t('incremental_onboarding.registration.fields.incorporationDate.label')"
      id="incorporation-date"
      data-testid="incorporation-date"
      :show-mandatory="true"
      :value="getFormattedDate(getIncorporationDate)"
      :set-value="setIncorporationDate"
      :error="getErrors.incorporationDate"
      :hint="$t('incremental_onboarding.registration.fields.incorporationDate.instruction')"
    />
  </onboarding-form-layout>
</template>

<script>
import merchantOnboardingApi from "@/api/merchantOnboardingApi";
import { OwnershipType } from "@/api/paymentApi";
import DateInput from "@/components/common/DateInput";
import InputField from "@/components/common/InputField";
import OnboardingFormLayout from "@/components/incremental-onboarding/OnboardingFormLayout.vue";
import { OnboardingFormMixin } from "@/components/incremental-onboarding/OnboardingFormMixin";
import BusinessOwnership from "@/components/onboarding/BusinessOwnership.vue";
import OwnershipTypeList from "@/data/ownership-type-list";
import TypesOfBusinessList from "@/data/type-of-business-list.ts";
import { BusinessProfileField } from "@/store/modules/incremental-onboarding/incrementalOnboardingbusinessProfile";
import { mapActions, mapGetters } from "vuex";
import ErrorCodeNotification from '@/components/incremental-onboarding/ErrorCodeNotification.vue';
import FullStoryHelper from '@/app/utils/fullstory';
import {BUSINESS_REGISTRATION_PROCEED, UPDATE_BUSINESS_REGISTRATION_PROCEED} from '@/app/utils/fullstory/constants';

export default {
  name: 'BusinessRegistration',
  components: { ErrorCodeNotification, DateInput, InputField, OnboardingFormLayout},
  mixins: [OnboardingFormMixin],
  props: {
    isUpdate: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorCodes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data: function () {
    return {
      showMCCNotSupportedPopup: false,
    }
  },
  computed: {
    OwnershipType() {
      return OwnershipType
    },
    BusinessOwnership() {
      return BusinessOwnership
    },
    BusinessProfileField() {
      return BusinessProfileField
    },
    getApplicableCountrySpecificApiFields() {
      return Object.keys(this.getCountrySpecificApiFields).filter(field => {
        return this.shouldShowApiField(field)
      })
    },
    ...mapGetters('app', ['getMerchantId']),
    ...mapGetters('incrementalOnboardingBusinessAddress', ['getCountry']),
    ...mapGetters('businessRegistration', [
      'getIncorporationDate',
      'getErrors',
      'getCountrySpecificApiFields',
      'getIncrementalOnboardingData',
      'shouldShowApiField',
      'isDirty',
    ]),
  },
  methods: {
    ...mapActions('businessRegistration', {
      setIncorporationDate: 'updateIncorporationDate',
      setCountrySpecificField: 'updateCountrySpecificField',
      validate: 'validate',
      setErrors: 'setErrors',
      resetDirty: 'resetDirty',
    }),
    getFormattedDate: (date) => {
      if (!date) return null;
      return date.replace(/(\d{4})-(\d\d)-(\d\d)/, "$2/$3/$1");
    },
    async proceed() {
      const apiCall = async () => {
        if (this.isUpdate) {
          return await merchantOnboardingApi.putRegistrationInfo(this.getMerchantId, this.getIncrementalOnboardingData);
        }
        return await merchantOnboardingApi.updateRegistrationInfo(this.getMerchantId, this.getIncrementalOnboardingData);
      };

      // Emit FullStory event
      FullStoryHelper.emitIncrementalOnboardingEvent(BUSINESS_REGISTRATION_PROCEED,
        UPDATE_BUSINESS_REGISTRATION_PROCEED, this.isUpdate);

      await this.submitForm(apiCall);
    },
    getTypesOfBusiness: () => TypesOfBusinessList,
    getOwnershipTypeList: () => OwnershipTypeList,
  },
}
</script>

<style lang="scss">
.registration-container {

  & .s-selectmenu-content {
    min-width: max-content;
  }

}


</style>